import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  BellIcon,
  XMarkIcon,
  CheckIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  TrashIcon,
} from '../../lib/commonUtils';
import {
  RefreshButton,
  PageHeader,
  SearchAndFilters,
  CustomDropdown,
} from '../../lib/commonUtils';
import api from '../../api/axiosInstance';

const safeNum = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);

const normalizeNotification = (n) => ({
  id: n.id,
  type: (n.type || 'info').toLowerCase(),
  title: n.title || 'Notification',
  message: n.message || '',
  timestamp: n.timestamp || new Date().toISOString(),
  read: Boolean(n.read ?? false),
});

const todayKey = () => new Date().toISOString().slice(0, 10);

const getNotificationIcon = (type) => {
  switch (type) {
    case 'alert':
      return <ExclamationTriangleIcon className="h-6 w-6 text-error-600" />;
    case 'warning':
      return <ExclamationTriangleIcon className="h-6 w-6 text-warning-600" />;
    case 'success':
      return <CheckCircleIcon className="h-6 w-6 text-success-600" />;
    case 'info':
    default:
      return <InformationCircleIcon className="h-6 w-6 text-primary-600" />;
  }
};

const formatTimestampRelative = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffInMinutes = Math.floor(diffMs / (1000 * 60));
  const diffInHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffInMinutes < 1) return 'just now';
  if (diffInHours < 1) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  } else if (diffInHours < 48) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString();
  }
};

const readPrevSubSnapshot = () => {
  try {
    const raw = localStorage.getItem('notif.prevSubscription');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writePrevSubSnapshot = (snap) => {
  try {
    localStorage.setItem('notif.prevSubscription', JSON.stringify(snap || {}));
  } catch { }
};

const readPersistedMap = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};
const writePersistedMap = (key, map) => {
  try {
    localStorage.setItem(key, JSON.stringify(map || {}));
  } catch { }
};

const KPISummarySkeleton = () => (
  <div className="grid grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {[...Array(3)].map((_, i) => (
      <div key={i} className="controlled-glass p-6 rounded-[32px] animate-pulse">
        <div className="flex items-center">
          <div className="h-10 w-10 rounded-xl bg-white/5 mr-4" />
          <div className="flex-1">
            <div className="h-3 w-16 bg-white/10 rounded-full mb-2" />
            <div className="h-6 w-12 bg-white/5 rounded-full" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const Notifications = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [readMap, setReadMap] = useState(() => readPersistedMap('notif.readMap'));
  const [dismissedMap, setDismissedMap] = useState(() => readPersistedMap('notif.dismissedMap'));
  const [firstSeenMap, setFirstSeenMap] = useState(() => readPersistedMap('notif.firstSeenMap'));

  useEffect(() => writePersistedMap('notif.readMap', readMap), [readMap]);
  useEffect(() => writePersistedMap('notif.dismissedMap', dismissedMap), [dismissedMap]);
  useEffect(() => writePersistedMap('notif.firstSeenMap', firstSeenMap), [firstSeenMap]);

  const markAsRead = (id) => {
    setReadMap((prev) => ({ ...prev, [id]: true }));
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const deleteNotification = (id) => {
    setDismissedMap((prev) => ({ ...prev, [id]: true }));
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const markAllAsRead = () => {
    const ids = notifications.map((n) => n.id);
    const next = { ...readMap };
    ids.forEach((id) => { next[id] = true; });
    setReadMap(next);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const clearAll = () => {
    if (!window.confirm('Are you sure you want to clear all notifications?')) return;
    const next = { ...dismissedMap };
    notifications.forEach((n) => { next[n.id] = true; });
    setDismissedMap(next);
    setNotifications([]);
  };

  const fetchUsage = async () => {
    try {
      const res = await api.get('/payments/current-subscription');
      const sub = res?.data?.subscription;
      if (!sub) return null;
      return {
        current_plan: sub.plan_name,
        minutes_limit: safeNum(sub.included_minutes),
        minutes_remaining: safeNum(sub.remaining_minutes),
        minutes_used: Math.max(0, safeNum(sub.included_minutes) - safeNum(sub.remaining_minutes)),
        updated_at: sub.created_at || sub.start_date,
      };
    } catch {
      return null;
    }
  };

  const fetchCurrentSub = async () => {
    try {
      const res = await api.get('/payments/current-subscription');
      return res?.data?.subscription || null;
    } catch {
      return null;
    }
  };

  const fetchPayments = async () => {
    try {
      let res;
      try {
        res = await api.get('/payments/subscription-history?limit=20');
      } catch {
        res = await api.get('/payments/subscription-history?limit=10');
      }
      return res?.data || null;
    } catch {
      return null;
    }
  };

  const fetchBatchJobs = async () => {
    try {
      const res = await api.get('/agents/batch-calling-status');
      return res?.data || null;
    } catch {
      return null;
    }
  };

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/dashboard-analysis');
      return res?.data || null;
    } catch {
      return null;
    }
  };

  const fetchCallHistory = async () => {
    try {
      const res = await api.get('/call-history', { params: { limit: 100 } });
      return Array.isArray(res?.data) ? res.data : [];
    } catch {
      return [];
    }
  };

  const fetchAll = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const prevSub = readPrevSubSnapshot();

      const [usage, currentSub, payments, jobs, dashboard, calls] = await Promise.allSettled([
        fetchUsage(),
        fetchCurrentSub(),
        fetchPayments(),
        fetchBatchJobs(),
        fetchDashboard(),
        fetchCallHistory(),
      ]);

      const pendingFirstSeen = {};

      const getStableTs = (id, serverTs) => {
        const existing = firstSeenMap[id];
        if (existing) return existing;
        const chosen = serverTs || new Date().toISOString();
        pendingFirstSeen[id] = chosen;
        return chosen;
      };

      const buildNotifications = ({
        usage,
        currentSub,
        prevSub,
        payments,
        jobs,
        dashboard,
        calls,
      }) => {
        const out = [];

        if (usage) {
          const used =
            safeNum(usage.minutes_used) ||
            safeNum(usage.used_minutes) ||
            safeNum(usage.current_usage_minutes);
          const limit =
            safeNum(usage.minutes_limit) ||
            safeNum(usage.limit_minutes) ||
            safeNum(usage.plan_minutes);
          const periodEnd = usage.period_end || usage.current_period_end || '';
          const serverTs = usage.updated_at || usage.timestamp;

          if (limit > 0) {
            const pct = (used / limit) * 100;
            if (pct >= 95) {
              const id = `usage|${periodEnd || todayKey()}|ALERT`;
              out.push(
                normalizeNotification({
                  id,
                  type: 'alert',
                  title: 'Minutes Almost Exhausted',
                  message: `You've used ${Math.round(used)}/${limit} minutes (${Math.round(pct)}%). Consider upgrading to avoid service interruption.`,
                  timestamp: getStableTs(id, serverTs),
                  read: Boolean(readMap[id]),
                })
              );
            } else if (pct >= 80) {
              const id = `usage|${periodEnd || todayKey()}|WARN`;
              out.push(
                normalizeNotification({
                  id,
                  type: 'warning',
                  title: 'High Usage Detected',
                  message: `You've used ${Math.round(used)}/${limit} minutes (${Math.round(pct)}%).`,
                  timestamp: getStableTs(id, serverTs),
                  read: Boolean(readMap[id]),
                })
              );
            }
          }
        }

        if (currentSub) {
          const planCode =
            (currentSub?.plan_code ||
              currentSub?.code ||
              currentSub?.plan?.code ||
              currentSub?.plan?.id ||
              currentSub?.current_plan ||
              currentSub?.subscription_plan ||
              '')
              .toString()
              .toLowerCase();

          const planName =
            currentSub?.plan_name ||
            currentSub?.name ||
            currentSub?.plan?.name ||
            currentSub?.subscription_plan ||
            planCode ||
            'Current Plan';

          const status = (currentSub?.status || currentSub?.subscription_status || '').toLowerCase();

          const subUpdatedAt =
            currentSub?.updated_at ||
            currentSub?.current_period_start ||
            currentSub?.created_at;

          if (prevSub) {
            const prevPlanCode = (prevSub.planCode || '').toLowerCase();
            const prevPlanName = prevSub.planName || prevPlanCode || 'Previous Plan';
            const prevStatus = (prevSub.status || '').toLowerCase();

            if (planCode && prevPlanCode && planCode !== prevPlanCode) {
              const id = `sub|plan|${planCode}`;
              out.push(
                normalizeNotification({
                  id,
                  type: 'info',
                  title: 'Subscription Plan Changed',
                  message: `Your subscription changed from "${prevPlanName}" to "${planName}".`,
                  timestamp: getStableTs(id, subUpdatedAt),
                  read: Boolean(readMap[id]),
                })
              );
            }

            if (status && status !== prevStatus) {
              const id = `sub|status|${status}`;
              const critical = ['past_due', 'unpaid', 'canceled', 'incomplete', 'incomplete_expired'];
              const positive = ['active', 'trialing'];
              const type = critical.includes(status)
                ? 'alert'
                : positive.includes(status)
                  ? 'success'
                  : 'info';

              out.push(
                normalizeNotification({
                  id,
                  type,
                  title: 'Subscription Status Updated',
                  message: `Status changed from "${prevStatus || 'unknown'}" to "${status}".`,
                  timestamp: getStableTs(id, subUpdatedAt),
                  read: Boolean(readMap[id]),
                })
              );
            }
          }
        }

        if (payments && Array.isArray(payments.payments)) {
          payments.payments.slice(0, 20).forEach((p) => {
            const status = (p.status || p.payment_status || '').toLowerCase();
            if (status.includes('fail')) {
              const created = p.created_at || p.invoice_date || p.timestamp;
              const id = `pay|${p.id || p.invoice_id || p.cursor || created}|fail`;
              out.push(
                normalizeNotification({
                  id,
                  type: 'alert',
                  title: 'Subscription Charge Failed',
                  message: `A recent subscription charge failed. Please update your billing details.`,
                  timestamp: getStableTs(id, created),
                  read: Boolean(readMap[id]),
                })
              );
            }
          });
        }

        if (jobs && Array.isArray(jobs.jobs)) {
          jobs.jobs.forEach((j) => {
            const status = String(j.status || j?.elevenlabs_live_status?.status || 'pending').toLowerCase();
            const created = j.created_at || j?.elevenlabs_live_status?.created_at || j?.elevenlabs_live_status?.created_at_unix
              ? new Date((j?.elevenlabs_live_status?.created_at_unix || 0) * 1000).toISOString()
              : j.created_at;
            const jobId = j.batch_job_id || j.id || `${j.agent_id}|${j.call_name}`;
            if (!jobId) return;

            if (status.includes('fail')) {
              const id = `job|${jobId}|fail`;
              out.push(
                normalizeNotification({
                  id,
                  type: 'alert',
                  title: 'Batch Job Failed',
                  message: `Batch calling "${j.call_name}" for ${j.agent_name} failed.`,
                  timestamp: getStableTs(id, created),
                  read: Boolean(readMap[id]),
                })
              );
            } else if (status.includes('complete') || status === 'done' || status === 'finished') {
              const id = `job|${jobId}|ok`;
              out.push(
                normalizeNotification({
                  id,
                  type: 'success',
                  title: 'Batch Job Completed',
                  message: `Batch calling "${j.call_name}" for ${j.agent_name} completed successfully.`,
                  timestamp: getStableTs(id, created),
                  read: Boolean(readMap[id]),
                })
              );
            } else if (
              status.includes('dispatch') ||
              status.includes('schedule') ||
              status.includes('progress') ||
              status === 'pending'
            ) {
              const id = `job|${jobId}|info`;
              out.push(
                normalizeNotification({
                  id,
                  type: 'info',
                  title: 'Batch Job In Progress',
                  message: `Batch calling "${j.call_name}" for ${j.agent_name}" is in progress.`,
                  timestamp: getStableTs(id, created),
                  read: Boolean(readMap[id]),
                })
              );
            }
          });
        }

        if (dashboard && dashboard.overview) {
          const sr = safeNum(dashboard.overview.success_rate_value, 0);
          const serverTs = dashboard.updated_at;
          if (sr > 0 && sr < 85) {
            const id = `perf|lowSR`;
            out.push(
              normalizeNotification({
                id,
                type: 'alert',
                title: 'Agent Performance Alert',
                message: `Overall success rate dropped to ${Math.round(sr)}%. Review recent calls and retrain agents if needed.`,
                timestamp: getStableTs(id, serverTs),
                read: Boolean(readMap[id]),
              })
            );
          }
        }

        if (Array.isArray(calls) && calls.length) {
          const today = todayKey();
          const callsToday = calls.filter(
            (c) => (new Date(c.created_at || c.timestamp || c.time)).toISOString().slice(0, 10) === today
          );
          if (callsToday.length >= 100) {
            const id = `calls|${today}|high`;

            const newestCallTs = callsToday
              .map((c) => c.created_at || c.timestamp || c.time)
              .filter(Boolean)
              .sort()
              .pop();
            out.push(
              normalizeNotification({
                id,
                type: 'warning',
                title: 'High Call Volume',
                message: `Unusually high call volume detected today (${callsToday.length} calls). Consider activating additional agents.`,
                timestamp: getStableTs(id, newestCallTs),
                read: Boolean(readMap[id]),
              })
            );
          }
        }

        out.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        return out.filter((n) => !dismissedMap[n.id]);
      };

      const next = buildNotifications({
        usage: usage.value || null,
        currentSub: currentSub.value || null,
        prevSub,
        payments: payments.value || null,
        jobs: jobs.value || null,
        dashboard: dashboard.value || null,
        calls: calls.value || [],
      });

      const merged = next.map((n) => ({ ...n, read: Boolean(readMap[n.id]) }));
      setNotifications(merged);

      if (Object.keys(pendingFirstSeen).length) {
        setFirstSeenMap((prev) => ({ ...prev, ...pendingFirstSeen }));
      }

      const cs = currentSub.value || null;
      if (cs) {
        const planCode =
          (cs?.plan_code ||
            cs?.code ||
            cs?.plan?.code ||
            cs?.plan?.id ||
            cs?.current_plan ||
            cs?.subscription_plan ||
            '')
            .toString()
            .toLowerCase();
        const planName =
          cs?.plan_name ||
          cs?.name ||
          cs?.plan?.name ||
          cs?.subscription_plan ||
          planCode ||
          '';
        const status = (cs?.status || cs?.subscription_status || '').toLowerCase();
        writePrevSubSnapshot({ planCode, planName, status });
      }
    } catch (e) {
      setErr(e?.message || 'Failed to build notifications');
    } finally {
      setLoading(false);
    }
  }, [readMap, dismissedMap, firstSeenMap]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filteredNotifications = useMemo(() => {
    const s = (searchTerm || '').toLowerCase().trim();
    return notifications.filter((notification) => {
      const matchesFilter =
        filter === 'all' ||
        (filter === 'unread' && !notification.read) ||
        (filter === 'read' && notification.read);

      const matchesSearch =
        !s ||
        notification.title.toLowerCase().includes(s) ||
        notification.message.toLowerCase().includes(s) ||
        notification.type.toLowerCase().includes(s);

      return matchesFilter && matchesSearch;
    });
  }, [notifications, filter, searchTerm]);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const todayCount = notifications.filter(
    (n) => new Date(n.timestamp).toDateString() === new Date().toDateString()
  ).length;

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title={
          <div className="flex items-center">
            <BellIcon className="h-6 w-6 mr-3 text-primary-400" />
            <span className="tracking-tight">Notifications</span>
            {unreadCount > 0 && !loading && (
              <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/10 text-red-400 border border-red-500/20 uppercase tracking-widest">
                {unreadCount} unread
              </span>
            )}
          </div>
        }
        subtitle="Stay updated with your agent performance, subscription changes, and system alerts"
      >
        <div className="flex justify-between sm:justify-start sm:space-x-3 w-full sm:w-auto">
          <RefreshButton onClick={fetchAll} isLoading={loading} className="btn-secondary" />
          <button onClick={markAllAsRead} className="btn btn-secondary px-6" disabled={notifications.length === 0}>
            <CheckIcon className="h-4 w-4" />
            <span>Mark All Read</span>
          </button>
          <button onClick={clearAll} className="btn btn-danger px-6" disabled={notifications.length === 0}>
            <TrashIcon className="h-4 w-4" />
            <span>Clear All</span>
          </button>
        </div>
      </PageHeader>

      {loading ? (
        <KPISummarySkeleton />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="controlled-glass p-6 rounded-[32px] inner-highlight">
            <div className="flex items-center">
              <div className="p-3 bg-primary-500/10 rounded-xl border border-primary-500/20">
                <BellIcon className="h-6 w-6 text-primary-400" />
              </div>
              <div className="ml-4">
                <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Total</p>
                <p className="text-2xl font-bold text-white tracking-tight">{notifications.length}</p>
              </div>
            </div>
          </div>
          <div className="controlled-glass p-6 rounded-[32px] inner-highlight">
            <div className="flex items-center">
              <div className="p-3 bg-red-500/10 rounded-xl border border-red-500/20">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Unread</p>
                <p className="text-2xl font-bold text-white tracking-tight">{unreadCount}</p>
              </div>
            </div>
          </div>
          <div className="controlled-glass p-6 rounded-[32px] inner-highlight">
            <div className="flex items-center">
              <div className="p-3 bg-blue-500/10 rounded-xl border border-blue-500/20">
                <ClockIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest">Today</p>
                <p className="text-2xl font-bold text-white tracking-tight">{todayCount}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="block sm:hidden">
        <div className="space-y-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search notifications..."
            disabled={loading}
            className="input"
          />
          <div className="flex flex-col space-y-3">
            <CustomDropdown
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Notifications' },
                { value: 'unread', label: 'Unread' },
                { value: 'read', label: 'Read' },
              ]}
              placeholder="All Notifications"
              className="text-sm"
            />
          </div>
        </div>
      </div>

      <div className="hidden sm:block">
        <SearchAndFilters
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          searchPlaceholder="Search notifications..."
          filters={[
            {
              value: filter,
              onChange: (e) => setFilter(e.target.value),
              options: [
                { value: 'all', label: 'All Notifications' },
                { value: 'unread', label: 'Unread' },
                { value: 'read', label: 'Read' },
              ],
            },
          ]}
          disabled={loading}
        />
      </div>

      {err && (
        <div className="controlled-glass p-4 rounded-2xl border border-red-500/20 text-sm text-red-400 font-medium inner-highlight">
          {err}
        </div>
      )}

      <div className="space-y-4">
        {loading ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="controlled-glass p-6 rounded-[32px] animate-pulse">
              <div className="flex items-start space-x-4">
                <div className="h-10 w-10 rounded-xl bg-white/5" />
                <div className="flex-1">
                  <div className="h-4 w-40 bg-white/10 rounded-full mb-3" />
                  <div className="h-3 w-3/4 bg-white/5 rounded-full mb-2" />
                  <div className="h-2 w-20 bg-white/5 rounded-full" />
                </div>
              </div>
            </div>
          ))
        ) : filteredNotifications.length === 0 ? (
          <div className="controlled-glass text-center py-20 rounded-[32px] inner-highlight">
            <div className="p-5 bg-white/5 border border-white/10 rounded-3xl w-fit mx-auto mb-6">
              <BellIcon className="h-10 w-10 text-white/20" />
            </div>
            <h3 className="text-xl font-bold text-white tracking-tight">No notifications found</h3>
            <p className="text-sm font-medium text-white/30 italic mt-2 max-w-sm mx-auto">
              {searchTerm || filter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : "You're all caught up! No notifications to display."}
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`controlled-glass p-6 rounded-[32px] hover:bg-white/[0.02] transition-all group inner-highlight ${!notification.read ? 'border-l-4 border-l-primary-500/50' : ''
                }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-5 flex-1">
                  <div className="p-3 bg-white/5 border border-white/10 rounded-2xl group-hover:bg-white/10 transition-colors">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base font-bold text-white truncate tracking-tight">
                        {notification.title}
                        {!notification.read && (
                          <span className="ml-3 inline-block w-2.5 h-2.5 bg-primary-500 rounded-full shadow-[0_0_10px_rgba(139,92,246,0.5)]" />
                        )}
                      </h3>
                    </div>
                    <p className="text-sm font-medium text-white/60 mb-3 leading-relaxed">{notification.message}</p>
                    <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest">{formatTimestampRelative(notification.timestamp)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  {!notification.read && (
                    <button
                      onClick={() => markAsRead(notification.id)}
                      className="p-2 bg-white/5 border border-white/10 rounded-xl text-white/40 hover:text-primary-400 hover:bg-primary-500/10 hover:border-primary-500/20 transition-all"
                      title="Mark as read"
                    >
                      <CheckIcon className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => deleteNotification(notification.id)}
                    className="p-2 bg-white/5 border border-white/10 rounded-xl text-white/40 hover:text-red-400 hover:bg-red-500/10 hover:border-red-500/20 transition-all"
                    title="Delete notification"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Notifications;
